import { audio } from '../../../../audio/AudioManager.js';
import { SaveManager } from '../../../../state/SaveManager.js';
import { NUMBER_KINGDOM_ASSETS } from '../NumberKingdomAssets.js';
import type { CrystalNumberBuilderConfig } from './CrystalNumberBuilderConfig.js';
import { CrystalNumberBuilderState, type PlaceValueKind } from './CrystalNumberBuilderState.js';
import {
  calculateChallengeSafeBounds,
  isCompactChallengeLayout,
  type ChallengeSafeBounds,
} from './NumberKingdomChallengeLayout.js';

const FONT_UI = '"Nunito", "Segoe UI", Arial, sans-serif';
const CENTER_X = 640;

interface BuilderCallbacks {
  onCancel: () => void;
  onComplete: (config: CrystalNumberBuilderConfig) => void;
}

interface StationDefinition {
  kind: PlaceValueKind;
  x: number;
  texture: string;
  pieceTexture: string;
  sourceWidth: number;
}

interface BuilderLayout {
  compact: boolean;
  safe: ChallengeSafeBounds;
  panelY: number;
  panelWidth: number;
  panelHeight: number;
  targetY: number;
  stationY: number;
  stationWidth: number;
  stationHeight: number;
  countY: number;
  sourceY: number;
  sourceLabelY: number;
  decompositionY: number;
  feedbackY: number;
  buttonY: number;
  checkX: number;
  hintX: number;
}

export class CrystalNumberBuilder {
  private readonly state: CrystalNumberBuilderState;
  private readonly container: Phaser.GameObjects.Container;
  private readonly stations = new Map<PlaceValueKind, Phaser.GameObjects.Image>();
  private readonly sources = new Map<PlaceValueKind, Phaser.GameObjects.Image>();
  private readonly sourceLabels = new Map<PlaceValueKind, Phaser.GameObjects.Text>();
  private readonly placedPieces = new Map<PlaceValueKind, Phaser.GameObjects.Image[]>();
  private readonly timers: Phaser.Time.TimerEvent[] = [];
  private readonly stationDefinitions: StationDefinition[] = [
    {
      kind: 'hundreds', x: 390,
      texture: NUMBER_KINGDOM_ASSETS.hundredsSlot.key,
      pieceTexture: NUMBER_KINGDOM_ASSETS.hundredBlock.key,
      sourceWidth: 70,
    },
    {
      kind: 'tens', x: 640,
      texture: NUMBER_KINGDOM_ASSETS.tensSlot.key,
      pieceTexture: NUMBER_KINGDOM_ASSETS.tenRod.key,
      sourceWidth: 38,
    },
    {
      kind: 'ones', x: 890,
      texture: NUMBER_KINGDOM_ASSETS.onesSlot.key,
      pieceTexture: NUMBER_KINGDOM_ASSETS.oneCube.key,
      sourceWidth: 50,
    },
  ];

  private decompositionText!: Phaser.GameObjects.Text;
  private feedbackText!: Phaser.GameObjects.Text;
  private countTexts = new Map<PlaceValueKind, Phaser.GameObjects.Text>();
  private panel!: Phaser.GameObjects.Image;
  private targetText!: Phaser.GameObjects.Text;
  private backButton!: Phaser.GameObjects.Text;
  private controlsText!: Phaser.GameObjects.Text;
  private checkButton!: Phaser.GameObjects.Image;
  private hintButton!: Phaser.GameObjects.Image;
  private correctBurst!: Phaser.GameObjects.Image;
  private incorrectPulse!: Phaser.GameObjects.Image;
  private energyOrb!: Phaser.GameObjects.Image;
  private currentLayout!: BuilderLayout;
  private hintIndex = 0;
  private inputLocked = false;
  private suspended = false;
  private destroyed = false;

  constructor(
    private readonly scene: Phaser.Scene,
    readonly config: CrystalNumberBuilderConfig,
    private readonly reducedMotion: boolean,
    private readonly callbacks: BuilderCallbacks,
  ) {
    this.state = new CrystalNumberBuilderState(config);
    this.container = scene.add.container(0, 0).setDepth(220).setAlpha(0);
    this.createInterface();
    this.registerKeyboard();
    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.destroy, this);
    this.scene.tweens.add({
      targets: this.container,
      alpha: 1,
      duration: this.reducedMotion ? 160 : 320,
      ease: 'Sine.easeOut',
    });
  }

  private createInterface() {
    this.panel = this.scene.add.image(CENTER_X, 252, NUMBER_KINGDOM_ASSETS.challengePanel.key);
    this.panel.setCrop(0, 0, 575, 236);
    this.panel.setDisplaySize(1100, 488);

    this.targetText = this.scene.add.text(CENTER_X, 218, `Build ${this.config.target}`, {
      fontFamily: FONT_UI,
      fontSize: '36px',
      fontStyle: 'bold',
      color: '#173a78',
      stroke: '#ffffff',
      strokeThickness: 5,
      shadow: { offsetX: 0, offsetY: 3, blur: 4, color: '#7c5520', fill: true },
    }).setOrigin(0.5);

    this.backButton = this.scene.add.text(205, 22, '←  BACK TO VALLEY', {
      fontFamily: FONT_UI,
      fontSize: '14px',
      fontStyle: 'bold',
      color: '#fff8dd',
      stroke: '#17351f',
      strokeThickness: 5,
      padding: { x: 10, y: 8 },
    }).setOrigin(0.5, 0).setInteractive({ useHandCursor: true });
    this.backButton.on('pointerup', () => this.requestCancel());

    this.controlsText = this.scene.add.text(CENTER_X, 22,
      'Drag or tap pieces  •  1 / 2 / 3 add  •  Shift removes  •  Enter checks  •  H hints', {
      fontFamily: FONT_UI,
      fontSize: '12px',
      fontStyle: 'bold',
      color: '#fff8dd',
      stroke: '#17351f',
      strokeThickness: 4,
    }).setOrigin(0.5, 0);

    this.container.add([this.panel, this.targetText, this.backButton, this.controlsText]);
    this.stationDefinitions.forEach(definition => this.createStation(definition));

    this.decompositionText = this.scene.add.text(CENTER_X, 580, 'Place pieces to build the number.', {
      fontFamily: FONT_UI,
      fontSize: '21px',
      fontStyle: 'bold',
      color: '#fff8cf',
      stroke: '#17351f',
      strokeThickness: 5,
    }).setOrigin(0.5);

    this.feedbackText = this.scene.add.text(CENTER_X, 613, '', {
      fontFamily: FONT_UI,
      fontSize: '17px',
      fontStyle: 'bold',
      align: 'center',
      color: '#fff2a6',
      stroke: '#17351f',
      strokeThickness: 5,
      wordWrap: { width: 720 },
    }).setOrigin(0.5);

    this.checkButton = this.scene.add.image(750, 674, NUMBER_KINGDOM_ASSETS.checkAnswerButton.key)
      .setAlpha(0.5)
      .setInteractive({ useHandCursor: true });
    this.checkButton.setCrop(12, 27, 326, 82);
    this.checkButton.setDisplaySize(230, 86);
    this.checkButton.on('pointerup', () => this.checkAnswer());

    this.hintButton = this.scene.add.image(1010, 674, NUMBER_KINGDOM_ASSETS.hintButton.key)
      .setInteractive({ useHandCursor: true });
    this.hintButton.setCrop(4, 16, 270, 75);
    this.hintButton.setDisplaySize(170, 66);
    this.hintButton.on('pointerup', () => this.showHint());

    this.correctBurst = this.scene.add.image(CENTER_X, 385, NUMBER_KINGDOM_ASSETS.correctBurst.key)
      .setVisible(false).setAlpha(0);
    this.correctBurst.setCrop(8, 20, 374, 164);
    this.correctBurst.setDisplaySize(390, 175);

    this.incorrectPulse = this.scene.add.image(CENTER_X, 385, NUMBER_KINGDOM_ASSETS.incorrectPulse.key)
      .setVisible(false).setAlpha(0);
    this.incorrectPulse.setCrop(8, 20, 354, 166);
    this.incorrectPulse.setDisplaySize(300, 170);

    this.energyOrb = this.scene.add.image(CENTER_X, 385, NUMBER_KINGDOM_ASSETS.numberEnergyOrb.key)
      .setVisible(false).setAlpha(0);
    this.energyOrb.setCrop(10, 20, 295, 166);
    this.energyOrb.setDisplaySize(230, 153);

    this.container.add([
      this.decompositionText,
      this.feedbackText,
      this.checkButton,
      this.hintButton,
      this.incorrectPulse,
      this.correctBurst,
      this.energyOrb,
    ]);
    this.layout();
  }

  private createStation(definition: StationDefinition) {
    const station = this.scene.add.image(definition.x, 385, definition.texture)
      .setDisplaySize(170, 200);
    this.stations.set(definition.kind, station);
    this.placedPieces.set(definition.kind, []);

    const count = this.scene.add.text(definition.x, 486, '0 placed', {
      fontFamily: FONT_UI,
      fontSize: '15px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#17351f',
      strokeThickness: 4,
    }).setOrigin(0.5);
    this.countTexts.set(definition.kind, count);

    const source = this.scene.add.image(definition.x, 538, definition.pieceTexture)
      .setInteractive({ useHandCursor: true });
    if (definition.kind === 'ones') source.setCrop(0, 0, 160, 153);
    source.setDisplaySize(
      definition.sourceWidth,
      definition.sourceWidth * (source.height / source.width),
    );
    source.setData('homeX', source.x);
    source.setData('homeY', source.y);
    source.setData('baseScaleX', source.scaleX);
    source.setData('baseScaleY', source.scaleY);
    this.scene.input.setDraggable(source);
    this.registerSourceInput(source, definition.kind);
    this.sources.set(definition.kind, source);

    const sourceLabel = this.scene.add.text(definition.x, 559, `Tap or drag  •  ${this.keyFor(definition.kind)}`, {
      fontFamily: FONT_UI,
      fontSize: '11px',
      fontStyle: 'bold',
      color: '#fff8dd',
      stroke: '#17351f',
      strokeThickness: 3,
    }).setOrigin(0.5, 0);
    this.sourceLabels.set(definition.kind, sourceLabel);

    this.container.add([station, count, source, sourceLabel]);
  }

  layout() {
    if (this.destroyed || !this.panel) return;
    const layout = this.calculateLayout();
    this.currentLayout = layout;
    const { compact, safe } = layout;
    const stationSpan = Math.min(compact ? 225 : 250, (safe.width - 220) * 0.5);

    this.panel.setPosition(safe.centerX, layout.panelY)
      .setDisplaySize(layout.panelWidth, layout.panelHeight);
    this.targetText.setPosition(safe.centerX, layout.targetY)
      .setFontSize(compact ? 32 : 36);
    this.backButton.setPosition(Math.max(205, safe.x + 90), compact ? safe.y + 2 : 22);
    this.controlsText.setPosition(safe.centerX, compact ? safe.y + 2 : 22)
      .setFontSize(compact ? 11 : 12);

    this.stationDefinitions.forEach((definition, index) => {
      definition.x = safe.centerX + (index - 1) * stationSpan;
      const station = this.stations.get(definition.kind)!;
      const count = this.countTexts.get(definition.kind)!;
      const source = this.sources.get(definition.kind)!;
      const sourceLabel = this.sourceLabels.get(definition.kind)!;
      const sourceWidth = definition.sourceWidth * (compact ? 0.9 : 1);

      station.setPosition(definition.x, layout.stationY)
        .setDisplaySize(layout.stationWidth, layout.stationHeight);
      count.setPosition(definition.x, layout.countY)
        .setFontSize(compact ? 14 : 15);

      this.scene.tweens.killTweensOf(source);
      source.setData('dragging', false).setDepth(0).setPosition(definition.x, layout.sourceY);
      source.setDisplaySize(sourceWidth, sourceWidth * (source.height / source.width));
      source.setData('homeX', source.x);
      source.setData('homeY', source.y);
      source.setData('baseScaleX', source.scaleX);
      source.setData('baseScaleY', source.scaleY);
      this.setMinimumHitArea(source, 82, 72);
      sourceLabel.setPosition(definition.x, layout.sourceLabelY)
        .setFontSize(compact ? 10 : 11);
    });

    this.decompositionText.setPosition(safe.centerX, layout.decompositionY)
      .setFontSize(compact ? 19 : 21);
    this.feedbackText.setPosition(safe.centerX, layout.feedbackY)
      .setFontSize(compact ? 15 : 17)
      .setWordWrapWidth(Math.min(720, safe.width - 80));

    this.checkButton.setPosition(layout.checkX, layout.buttonY)
      .setDisplaySize(compact ? 210 : 230, compact ? 74 : 86);
    this.hintButton.setPosition(layout.hintX, layout.buttonY)
      .setDisplaySize(compact ? 160 : 170, compact ? 60 : 66);
    this.correctBurst.setPosition(safe.centerX, layout.stationY)
      .setDisplaySize(compact ? 340 : 390, compact ? 153 : 175);
    this.incorrectPulse.setPosition(safe.centerX, layout.stationY)
      .setDisplaySize(compact ? 270 : 300, compact ? 153 : 170);
    this.energyOrb.setPosition(safe.centerX, layout.stationY)
      .setDisplaySize(compact ? 205 : 230, compact ? 136 : 153);

    this.stationDefinitions.forEach(definition => this.renderPlacedPieces(definition.kind, false));
  }

  private calculateLayout(): BuilderLayout {
    const safe = calculateChallengeSafeBounds(this.scene);
    const compact = isCompactChallengeLayout(this.scene, safe);
    if (!compact) {
      const panelWidth = Math.min(1100, safe.width);
      return {
        compact,
        safe,
        panelY: 252,
        panelWidth,
        panelHeight: panelWidth * (488 / 1100),
        targetY: 218,
        stationY: 385,
        stationWidth: 170,
        stationHeight: 200,
        countY: 486,
        sourceY: 538,
        sourceLabelY: 559,
        decompositionY: 580,
        feedbackY: 613,
        buttonY: 674,
        checkX: safe.centerX + 110,
        hintX: Math.min(safe.centerX + 370, safe.right - 88),
      };
    }

    const extraHeightOffset = Phaser.Math.Clamp((safe.height - 630) * 0.24, 0, 20);
    const panelWidth = Math.min(1020, safe.width);
    const panelHeight = Math.min(430, panelWidth / 2.35);
    const stationY = safe.y + 315 + extraHeightOffset;
    const buttonY = safe.bottom - 42;
    return {
      compact,
      safe,
      panelY: safe.y + panelHeight * 0.5,
      panelWidth,
      panelHeight,
      targetY: safe.y + 165 + extraHeightOffset * 0.35,
      stationY,
      stationWidth: 155,
      stationHeight: 182,
      countY: stationY + 92,
      sourceY: stationY + 134,
      sourceLabelY: stationY + 160,
      decompositionY: buttonY - 92,
      feedbackY: buttonY - 58,
      buttonY,
      checkX: safe.centerX - 85,
      hintX: safe.centerX + 150,
    };
  }

  private setMinimumHitArea(image: Phaser.GameObjects.Image, minimumWidth: number, minimumHeight: number) {
    const scaleX = Math.max(Math.abs(image.scaleX), 0.001);
    const scaleY = Math.max(Math.abs(image.scaleY), 0.001);
    const hitWidth = Math.max(image.width, minimumWidth / scaleX);
    const hitHeight = Math.max(image.height, minimumHeight / scaleY);
    image.setInteractive(
      new Phaser.Geom.Rectangle(
        (image.width - hitWidth) * 0.5,
        (image.height - hitHeight) * 0.5,
        hitWidth,
        hitHeight,
      ),
      Phaser.Geom.Rectangle.Contains,
    );
  }

  private registerSourceInput(source: Phaser.GameObjects.Image, kind: PlaceValueKind) {
    source.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.canInteract()) return;
      this.scene.tweens.killTweensOf(source);
      source.setData('pointerDownX', pointer.x);
      source.setData('pointerDownY', pointer.y);
    });
    source.on('dragstart', () => {
      if (!this.canInteract()) return;
      source.setData('dragging', true);
      source.setScale(
        (source.getData('baseScaleX') as number) * (this.reducedMotion ? 1.02 : 1.1),
        (source.getData('baseScaleY') as number) * (this.reducedMotion ? 1.02 : 1.1),
      );
      source.setDepth(20);
    });
    source.on('drag', (_pointer: Phaser.Input.Pointer, dragX: number, dragY: number) => {
      if (!this.canInteract()) return;
      source.setPosition(dragX, dragY);
    });
    source.on('dragend', (pointer: Phaser.Input.Pointer) => {
      if (!source.getData('dragging')) return;
      source.setData('dragging', false);
      const distance = Phaser.Math.Distance.Between(
        source.getData('pointerDownX') as number,
        source.getData('pointerDownY') as number,
        pointer.x,
        pointer.y,
      );
      if (distance < 12) {
        if (this.canInteract()) this.addPiece(kind);
        this.returnSource(source);
        return;
      }
      const station = this.stations.get(kind);
      const valid = station?.getBounds().contains(source.x, source.y) ?? false;
      if (valid && this.canInteract()) this.addPiece(kind);
      else if (this.canInteract()) this.showTemporaryFeedback('That piece belongs in its matching place-value station.');
      this.returnSource(source);
    });
  }

  private returnSource(source: Phaser.GameObjects.Image) {
    this.scene.tweens.killTweensOf(source);
    this.scene.tweens.add({
      targets: source,
      x: source.getData('homeX') as number,
      y: source.getData('homeY') as number,
      scaleX: source.getData('baseScaleX') as number,
      scaleY: source.getData('baseScaleY') as number,
      duration: this.reducedMotion ? 100 : 220,
      ease: 'Sine.easeOut',
      onComplete: () => source.setDepth(0),
    });
  }

  private addPiece(kind: PlaceValueKind) {
    if (!this.canInteract() || !this.state.add(kind)) return;
    audio.click();
    this.feedbackText.setText('');
    this.renderPlacedPieces(kind, true);
    this.updateDisplay();
  }

  private removePiece(kind: PlaceValueKind) {
    if (!this.canInteract() || !this.state.remove(kind)) return;
    audio.click();
    this.feedbackText.setText('');
    this.renderPlacedPieces(kind, false);
    this.updateDisplay();
  }

  private renderPlacedPieces(kind: PlaceValueKind, animateNewest: boolean) {
    const oldPieces = this.placedPieces.get(kind) ?? [];
    oldPieces.forEach(piece => {
      this.scene.tweens.killTweensOf(piece);
      piece.destroy();
    });

    const definition = this.stationDefinitions.find(item => item.kind === kind)!;
    const count = this.state.counts[kind];
    const pieces: Phaser.GameObjects.Image[] = [];
    for (let index = 0; index < count; index++) {
      const position = this.piecePosition(kind, definition.x, index);
      const piece = this.scene.add.image(position.x, position.y, definition.pieceTexture)
        .setInteractive({ useHandCursor: true });
      if (kind === 'ones') piece.setCrop(0, 0, 160, 153);
      piece.setDisplaySize(position.width, position.width * (piece.height / piece.width));
      this.setMinimumHitArea(piece, 52, 52);
      piece.on('pointerup', () => this.removePiece(kind));
      this.container.add(piece);
      pieces.push(piece);

      if (animateNewest && index === count - 1 && !this.reducedMotion) {
        const finalScaleX = piece.scaleX;
        const finalScaleY = piece.scaleY;
        piece.setScale(finalScaleX * 0.86, finalScaleY * 0.86);
        this.scene.tweens.add({
          targets: piece,
          scaleX: finalScaleX,
          scaleY: finalScaleY,
          duration: 170,
          ease: 'Back.easeOut',
        });
      }
    }
    this.placedPieces.set(kind, pieces);
  }

  private piecePosition(kind: PlaceValueKind, centerX: number, index: number) {
    const compact = this.currentLayout?.compact ?? false;
    const stationY = this.currentLayout?.stationY ?? 385;
    if (kind === 'hundreds') {
      const columns = 3;
      return {
        x: centerX + (index % columns - 1) * (compact ? 37 : 42),
        y: stationY + 3 + Math.floor(index / columns) * (compact ? 29 : 32),
        width: compact ? 46 : 52,
      };
    }
    if (kind === 'tens') {
      const columns = 5;
      return {
        x: centerX + (index % columns - 2) * (compact ? 20 : 23),
        y: stationY + 13 + Math.floor(index / columns) * (compact ? 39 : 44),
        width: compact ? 21 : 24,
      };
    }
    const columns = 3;
    return {
      x: centerX + (index % columns - 1) * (compact ? 33 : 38),
      y: stationY + 5 + Math.floor(index / columns) * (compact ? 30 : 33),
      width: compact ? 30 : 34,
    };
  }

  private updateDisplay() {
    this.countTexts.forEach((text, kind) => text.setText(`${this.state.counts[kind]} placed`));
    this.decompositionText.setText(
      this.state.hasPieces ? this.state.decomposition : 'Place pieces to build the number.',
    );
    this.checkButton.setAlpha(this.state.hasPieces ? 1 : 0.5);
  }

  private checkAnswer() {
    if (!this.canInteract() || !this.state.hasPieces) return;
    if (this.state.isCorrect) {
      SaveManager.recordAnswer(this.config.skillId, true);
      this.container.bringToTop(this.correctBurst);
      this.container.bringToTop(this.energyOrb);
      this.playCorrectResponse();
      return;
    }

    SaveManager.recordAnswer(this.config.skillId, false);
    this.container.bringToTop(this.incorrectPulse);
    this.feedbackText.setColor('#fff0b0');
    this.feedbackText.setText('Almost! Check the hundreds, tens, and ones.');
    this.scene.tweens.killTweensOf(this.incorrectPulse);
    this.incorrectPulse.setVisible(true).setAlpha(0).setScale(this.reducedMotion ? 1 : 0.92);
    this.scene.tweens.add({
      targets: this.incorrectPulse,
      alpha: 0.8,
      scale: 1,
      duration: this.reducedMotion ? 120 : 220,
      yoyo: true,
      hold: this.reducedMotion ? 80 : 180,
      ease: 'Sine.easeInOut',
      onComplete: () => this.incorrectPulse.setVisible(false),
    });
  }

  private showHint() {
    if (!this.canInteract()) return;
    const hints = [
      'Look at the digit in the hundreds place.',
      `${this.config.target} has ${this.state.required.hundreds} hundreds.`,
      'Now check how many tens and ones you need.',
    ];
    const copy = hints[Math.min(this.hintIndex, hints.length - 1)];
    this.hintIndex = Math.min(this.hintIndex + 1, hints.length - 1);
    this.feedbackText.setColor('#fff2a6').setText(`Hint: ${copy}`);
    audio.hint();
  }

  private playCorrectResponse() {
    this.inputLocked = true;
    audio.correct();
    this.feedbackText.setColor('#dfffdc').setText(`You built ${this.config.target}! The bridge energy is ready.`);
    this.correctBurst.setVisible(true).setAlpha(0).setScale(this.reducedMotion ? 1 : 0.86);
    this.energyOrb.setVisible(true).setAlpha(0).setScale(this.reducedMotion ? 1 : 0.78);
    this.scene.tweens.add({
      targets: this.correctBurst,
      alpha: 1,
      scale: 1,
      duration: this.reducedMotion ? 160 : 320,
      ease: 'Back.easeOut',
    });
    this.scene.tweens.add({
      targets: this.energyOrb,
      alpha: 1,
      scale: 1,
      duration: this.reducedMotion ? 220 : 520,
      ease: 'Sine.easeOut',
    });

    this.timers.push(this.scene.time.delayedCall(this.reducedMotion ? 700 : 1050, () => {
      const config = this.config;
      this.destroy();
      this.callbacks.onComplete(config);
    }));
  }

  private showTemporaryFeedback(copy: string) {
    this.feedbackText.setColor('#fff2a6').setText(copy);
    this.timers.push(this.scene.time.delayedCall(1600, () => {
      if (!this.destroyed && this.feedbackText.text === copy) this.feedbackText.setText('');
    }));
  }

  private registerKeyboard() {
    this.scene.input.keyboard?.on('keydown', this.handleKeyDown, this);
  }

  private handleKeyDown(event: KeyboardEvent) {
    if (!this.canInteract()) return;
    const kindByCode: Partial<Record<string, PlaceValueKind>> = {
      Digit1: 'hundreds', Numpad1: 'hundreds',
      Digit2: 'tens', Numpad2: 'tens',
      Digit3: 'ones', Numpad3: 'ones',
    };
    const kind = kindByCode[event.code];
    if (kind) {
      event.preventDefault();
      if (event.shiftKey) this.removePiece(kind);
      else this.addPiece(kind);
      return;
    }
    if (event.code === 'Enter' || event.code === 'NumpadEnter') {
      event.preventDefault();
      this.checkAnswer();
    } else if (event.code === 'KeyH') {
      event.preventDefault();
      this.showHint();
    } else if (event.code === 'Escape') {
      event.preventDefault();
      this.requestCancel();
    }
  }

  private requestCancel() {
    if (this.destroyed || this.inputLocked) return;
    this.destroy();
    this.callbacks.onCancel();
  }

  setSuspended(suspended: boolean) {
    this.suspended = suspended;
  }

  private canInteract() {
    return !this.destroyed && !this.inputLocked && !this.suspended;
  }

  private keyFor(kind: PlaceValueKind) {
    if (kind === 'hundreds') return '1';
    if (kind === 'tens') return '2';
    return '3';
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    this.scene.input.keyboard?.off('keydown', this.handleKeyDown, this);
    this.scene.events.off(Phaser.Scenes.Events.SHUTDOWN, this.destroy, this);
    this.timers.forEach(timer => timer.remove(false));
    this.timers.length = 0;
    this.scene.tweens.killTweensOf(this.container.getAll());
    this.scene.tweens.killTweensOf(this.container);
    this.container.destroy(true);
  }
}
